import { checkUserSession } from "../plugins/common.js";
import { settings } from "../plugins/settings.js";
import { bulkUpdateData, bulkUpdateFormData, deleteData, getData, getInfo, getRawData, readData, searchQueryData, searchJsonData, updateData, updateFormData, writeData, writeFormData } from "./database.js";

export var tablesData = {
	currentUser: null,
	currentPlant: null,
	currentCarrier: null,
	currentSupplier: null,
	currentEmployee: null,
	currentAddressee: null,
	currentAlternativeAddressee: null,
	currentWarehouse: null,
	currentLocation: null,
	currentItem: null,
	currentProject: null,
	currentProjectRow: null,
	currentPrinter: null,
	currentPrintTemplate: null,
	printerActionResult: null,
	users: [],
	plants: [],
	carriers: [],
	suppliers: [],
	employees: [],
	addressees: [],
	alternativeAddressees: [],
	warehouses: [],
	locations: [],
	items: [],
	projects: [],
	projectRows: [],
	printers: [],
	printTemplates: [],
};

export var tablesToBeRefreshed = {
	users: false,
	plants: false,
	carriers: false,
	suppliers: false,
	employees: false,
	addressees: false,
	alternativeAddressees: false,
	warehouses: false,
	locations: false,
	items: false,
	projects: false,
	projectRows: false,
	printers: false,
	printTemplates: false,
};

async function withErrorHandling(fn, errorReturn = false) {
	try {
		return await fn();
	} catch (error) {
		checkUserSession(error, "warning");
		return errorReturn;
	}
}

async function fetchList(fetchFn, apiName, storeKey) {
	return withErrorHandling(async () => {
		tablesData[storeKey] = await fetchFn(apiName);
		return true;
	});
}

async function readRecord(apiName, storeKey, id) {
	return withErrorHandling(async () => {
		tablesData[storeKey] = await readData(apiName, id);
		return true;
	});
}

async function createRecord(apiName, storeKey, objData, refreshFn, shouldRefresh, writeFn = writeData) {
	return withErrorHandling(async () => {
		const data = await writeFn(apiName, objData);
		if (shouldRefresh) await refreshFn();
		tablesData[storeKey] = data;
		return true;
	});
}

async function updateRecord(apiName, storeKey, id, objData, refreshFn, shouldRefresh, updateFn = updateData) {
	return withErrorHandling(async () => {
		const data = await updateFn(apiName, id, objData);
		if (shouldRefresh) await refreshFn();
		tablesData[storeKey] = data;
		return true;
	});
}

async function deleteRecord(apiName, storeKey, id, refreshFn, shouldRefresh) {
	return withErrorHandling(async () => {
		await deleteData(apiName, id);
		if (shouldRefresh) await refreshFn();
		tablesData[storeKey] = null;
		return true;
	});
}

async function querySearch(apiName, storeKey, params, single = false) {
	return withErrorHandling(async () => {
		const data = await searchQueryData(apiName, { params });
		tablesData[storeKey] = single ? data[0] : data;
		return true;
	});
}

async function getInformations(tableName) {
	return withErrorHandling(async () => {
		const data = await getInfo(tableName);
		return data;
	}, null);
}

// --- hasPrinter ---

export async function hasPrinter() {
	return withErrorHandling(async () => {
		const data = await getData("printers");
		settings.global.printerManagement = data.length > 0;
		tablesData.printers = data;
		return true;
	});
}

// --- Fetch lists ---

export const getUsers = () => fetchList(getData, "users", "users");
export const getPlants = () => fetchList(getData, "plants", "plants");
export const getCarriers = () => fetchList(getData, "carriers", "carriers");
export const getSuppliers = () => fetchList(getData, "suppliers", "suppliers");
export const getEmployees = () => fetchList(getData, "employees", "employees");
export const getAddressees = () => fetchList(getData, "addressees", "addressees");
export const getAlternativeAddressees = () => fetchList(getData, "alternative_addressees", "alternativeAddressees");
export const getWarehouses = () => fetchList(getData, "warehouses", "warehouses");
export const getLocations = () => fetchList(getData, "locations", "locations");
export const getItems = () => fetchList(getData, "items", "items");
export const getProjects = () => fetchList(getData, "projects", "projects");
export const getProjectRows = () => fetchList(getData, "project_rows", "projectRows");
export const getPrinters = () => fetchList(getData, "printers", "printers");
export const getPrintTemplates = () => fetchList(getData, "print_templates", "printTemplates");

// --- Fetch raw lists ---

export const getRawUsers = () => fetchList(getRawData, "users", "users");
export const getRawPlants = () => fetchList(getRawData, "plants", "plants");
export const getRawCarriers = () => fetchList(getRawData, "carriers", "carriers");
export const getRawSuppliers = () => fetchList(getRawData, "suppliers", "suppliers");
export const getRawEmployees = () => fetchList(getRawData, "employees", "employees");
export const getRawAddressees = () => fetchList(getRawData, "addressees", "addressees");
export const getRawAlternativeAddressees = () => fetchList(getRawData, "alternative_addressees", "alternativeAddressees");
export const getRawWarehouses = () => fetchList(getRawData, "warehouses", "warehouses");
export const getRawLocations = () => fetchList(getRawData, "locations", "locations");
export const getRawItems = () => fetchList(getRawData, "items", "items");
export const getRawProjects = () => fetchList(getRawData, "projects", "projects");
export const getRawProjectRows = () => fetchList(getRawData, "project_rows", "projectRows");
export const getRawPrinters = () => fetchList(getRawData, "printers", "printers");
export const getRawPrintTemplates = () => fetchList(getRawData, "print_templates", "printTemplates");

// --- Read single records ---

export const getUser = (id) => readRecord("users", "currentUser", id);
export const getPlant = (id) => readRecord("plants", "currentPlant", id);
export const getAlternativeAddressee = (id) => readRecord("alternative_addressees", "currentAlternativeAddressee", id);
export const getWarehouse = (id) => readRecord("warehouses", "currentWarehouse", id);
export const getLocation = (id) => readRecord("locations", "currentLocation", id);
export const getItem = (id) => readRecord("items", "currentItem", id);
export const getProject = (id) => readRecord("projects", "currentProject", id);
export const getProjectRow = (id) => readRecord("project_rows", "currentProjectRow", id);
export const getPrinter = (id) => readRecord("printers", "currentPrinter", id);
export const getPrintTemplate = (id) => readRecord("print_templates", "currentPrintTemplate", id);

// --- Create records ---

export const createCarrier = (objData, refreshData = true) => createRecord("carriers", "currentCarrier", objData, getCarriers, refreshData);
export const createSupplier = (objData, refreshData = true) => createRecord("suppliers", "currentSupplier", objData, getSuppliers, refreshData);
export const createEmployee = (objData, refreshData = true) => createRecord("employees", "currentEmployee", objData, getEmployees, refreshData);
export const createAddressee = (objData, refreshData = true) => createRecord("addressees", "currentAddressee", objData, getAddressees, refreshData);
export const createAlternativeAddressee = (objData, refreshData = true) => createRecord("alternative_addressees", "currentAlternativeAddressee", objData, getAlternativeAddressees, refreshData);
export const createWarehouse = (objData, refreshData = true) => createRecord("warehouses", "currentWarehouse", objData, getWarehouses, refreshData);
export const createLocation = (objData, refreshData = true) => createRecord("locations", "currentLocation", objData, getLocations, refreshData);
export const createItem = (objData, refreshData = true) => createRecord("items", "currentItem", objData, getItems, refreshData);
export const createProject = (objData, refreshData = true) => createRecord("projects", "currentProject", objData, getProjects, refreshData);
export const createProjectRow = (objData, refreshData = true) => createRecord("project_rows", "currentProjectRow", objData, getProjectRows, refreshData);
export const createItemFormData = (objData, refreshData = true) => createRecord("items", "currentItem", objData, getItems, refreshData, writeFormData);
export const createProjectRowFormData = (objData, refreshData = true) => createRecord("project_rows", "currentProjectRow", objData, getProjectRows, refreshData, writeFormData);

// --- Update records ---

export const updateUser = (id, objData, refreshData = true) => updateRecord("users", "currentUser", id, objData, getUsers, refreshData);
export const updateItem = (id, objData, refreshData = true) => updateRecord("items", "currentItem", id, objData, getItems, refreshData);
export const updateProject = (id, objData, refreshData = true) => updateRecord("projects", "currentProject", id, objData, getProjects, refreshData);
export const updateProjectRow = (id, objData, refreshData = true) => updateRecord("project_rows", "currentProjectRow", id, objData, getProjectRows, refreshData);
export const updatePrinter = (id, objData, refreshData = true) => updateRecord("printers", "currentPrinter", id, objData, getPrinters, refreshData);
export const updateItemFormData = (id, objData, refreshData = true) => updateRecord("items", "currentItem", id, objData, getItems, refreshData, updateFormData);
export const updateProjectRowFormData = (id, objData, refreshData = true) => updateRecord("project_rows", "currentProjectRow", id, objData, getProjectRows, refreshData, updateFormData);

export async function updateProjectBulk(id, objData, refreshData = true) {
	return withErrorHandling(async () => {
		const data = await bulkUpdateData("projects", id, objData);
		if (refreshData) await getProjects();
		return data.updated_records;
	}, -1);
}

export async function updateProjectFormDataBulk(id, objData, refreshData = true) {
	return withErrorHandling(async () => {
		const data = await bulkUpdateFormData("projects", id, objData);
		if (refreshData) await getProjects();
		return data.updated_records;
	}, -1);
}

// --- Delete records ---

export const deleteItem = (id, refreshData = true) => deleteRecord("items", "currentItem", id, getItems, refreshData);
export const deleteProject = (id, refreshData = true) => deleteRecord("projects", "currentProject", id, getProjects, refreshData);
export const deleteProjectRow = (id, refreshData = true) => deleteRecord("project_rows", "currentProjectRow", id, getProjectRows, refreshData);

// --- Search (list results) ---

export async function searchAlternativeAddressees(employee_id) {
	return querySearch("alternative_addressees", "alternativeAddressees", {
		'q[employee_id_eq]': employee_id,
	});
}

export async function searchWarehouses(plant_id) {
	return querySearch("warehouses", "warehouses", {
		'q[plant_id_eq]': plant_id,
	});
}

export async function searchLocations(warehouse_id) {
	return querySearch("locations", "locations", {
		'q[warehouse_id_eq]': warehouse_id,
	});
}

export async function searchProjectRows(code = null, location_code = null, closed = null) {
	const params = {};
	if (code != null) params['q[code_eq]'] = code;
	if (location_code != null) params['q[location_code_eq]'] = location_code;
	if (closed != null) params['q[closed_true]'] = closed;
	return querySearch("project_rows", "projectRows", params);
}

export async function jsonSearchProjects(json) {
	return withErrorHandling(async () => {
		tablesData.projects = await searchJsonData("projects", json);
		return true;
	});
}

export async function jsonSearchProjectRows(json) {
	return withErrorHandling(async () => {
		tablesData.projectRows = await searchJsonData("project_rows", json);
		return true;
	});
}

// --- Search (single result) ---

export async function searchWarehouse(plant_id = null, name = null, code = null) {
	const params = {};
	if (plant_id) params['q[plant_id_eq]'] = plant_id;
	if (name) params['q[name_eq]'] = name;
	if (code) params['q[code_eq]'] = code;
	return querySearch("warehouses", "currentWarehouse", params, true);
}

export async function searchLocation(name = null, code = null) {
	const params = {};
	if (name) params['q[name_eq]'] = name;
	if (code) params['q[code_eq]'] = code;
	return querySearch("locations", "currentLocation", params, true);
}

export async function searchItem(code = null, barcode = null) {
	const params = {};
	if (code) params['q[code_eq]'] = code;
	if (barcode) params['q[barcode_eq]'] = barcode;
	return querySearch("items", "currentItem", params, true);
}

export async function searchProject(code) {
	return querySearch("projects", "currentProject", { 'q[code_eq]': code }, true);
}

export async function searchProjectRow(code) {
	return querySearch("project_rows", "currentProjectRow", { 'q[code_eq]': code }, true);
}

export async function searchPrinter(isDefaultPrinter = null, printerName = null, printerPlantId = null) {
	const params = {};
	if (isDefaultPrinter != null) params['q[default_eq]'] = isDefaultPrinter;
	if (printerName != null) params['q[name_eq]'] = printerName;
	if (printerPlantId != null) params['q[plant_id_eq]'] = printerPlantId;
	return querySearch("printers", "currentPrinter", params, true);
}

// --- Printer action ---

export async function executePrinterAction(destinationUrl) {
	try {
		const data = await getData(destinationUrl);
		tablesData.printerActionResult = data;
		tablesData.printerIsOnErrorState = false;
	} catch (error) {
		checkUserSession(error, "warning");
		throw error;
	}
}

// --- Get info ---

export const getSchema = () => getInformations("schema");
export const getSettings = () => getInformations("settings");
export const getDsl = () => getInformations("dsl");
export const getNtp = () => getInformations("ntp");

export async function getPastDate(days) {
	const ntp = await getNtp();
	const reference = ntp?.server_time_utc ?? new Date().toISOString();
	const date = new Date(reference);
	date.setDate(date.getDate() - days);
	return date.toISOString();
}

// --- Bulk refresh ---

export async function getAllTablesData() {
	if (tablesToBeRefreshed.users) await getUsers();
	if (tablesToBeRefreshed.plants) await getPlants();
	if (tablesToBeRefreshed.carriers) await getCarriers();
	if (tablesToBeRefreshed.suppliers) await getSuppliers();
	if (tablesToBeRefreshed.employees) await getEmployees();
	if (tablesToBeRefreshed.addressees) await getAddressees();
	if (tablesToBeRefreshed.alternativeAddressees) await getAlternativeAddressees();
	if (tablesToBeRefreshed.warehouses) await getWarehouses();
	if (tablesToBeRefreshed.locations) await getLocations();
	if (tablesToBeRefreshed.items) await getItems();
	if (tablesToBeRefreshed.projects) await getProjects();
	if (tablesToBeRefreshed.projectRows) await getProjectRows();
	if (tablesToBeRefreshed.printers) await getPrinters();
	if (tablesToBeRefreshed.printTemplates) await getPrintTemplates();
}
